
import React, { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { X, User, Plus, Trash2, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { User as UserEntity } from '@/api/entities';
import { SEAT_PREFERENCE_OPTIONS, getDefaultSeatPreference } from '@/utils/seatPreference';
import { AIRLINE_PROGRAMS } from '@/constants/loyaltyPrograms';

const BLACKLIST_REASONS = [
  { value: 'defaulted_payments', label: 'Defaulted on payments' },
  { value: 'chargeback', label: 'Chargeback' },
  { value: 'fraud', label: 'Fraud / suspicious activity' },
  { value: 'policy_violation', label: 'Policy violation' },
  { value: 'abuse', label: 'Abusive behavior' },
  { value: 'other', label: 'Other' }
];

export default function PassengerEditModal({ isOpen, onClose, passengers, onSave, trip }) {
  const [editablePassengers, setEditablePassengers] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false); // New state to manage saving process
  const [isFormValid, setIsFormValid] = useState(true); // New state for form validation (assuming true by default for now)
  const [isAdmin, setIsAdmin] = useState(false);
  const [blacklistConfirmPassenger, setBlacklistConfirmPassenger] = useState(null);
  const [blacklistReasonKey, setBlacklistReasonKey] = useState('defaulted_payments');
  const [blacklistOtherReason, setBlacklistOtherReason] = useState('');
  const [isBlacklisting, setIsBlacklisting] = useState(false);
  const bookingId = useMemo(() => trip?.[0]?.id || trip?.[0]?.bookingId || null, [trip]);

  useEffect(() => {
    if (passengers && passengers.length > 0) {
      // Deep clone passengers to avoid mutating original objects
      // Using JSON parse/stringify to preserve all passenger fields
      setEditablePassengers(JSON.parse(JSON.stringify(passengers)));
      setIsFormValid(true); 
    } else {
      // If no passengers exist, initialize with one empty passenger form
      setEditablePassengers([{
        title: '',
        firstName: '',
        middleName: '',
        lastName: '',
        bornOn: '',
        gender: '',
        nationality: '',
        phoneNumber: '',
        email: '',
        passportNumber: '',
        passportExpiryDate: '',
        frequentFlyerProgram: '',
        frequentFlyerNumber: '',
        knownTravelerNumber: '',
        specialRequests: '',
        seatPreference: getDefaultSeatPreference(0)
      }]);
      setIsFormValid(true);
    }
  }, [passengers]);
  
  useEffect(() => {
    const checkAdmin = async () => {
      try {
        const me = await UserEntity.getCurrentUser();
        setIsAdmin(!!me && (me.role === 'admin' || me.app_metadata?.roles?.includes('admin')));
      } catch {
        setIsAdmin(false);
      }
    };
    checkAdmin();
  }, []);

  if (!isOpen) return null;

  const handlePassengerChange = (index, field, value) => {
    const newPassengers = [...editablePassengers];
    newPassengers[index][field] = value;
    setEditablePassengers(newPassengers);
    // You might want to re-run form validation here
    // setIsFormValid(validateUpdatedPassengers(newPassengers));
  };

  const addPassenger = () => {
    setEditablePassengers((prev) => [...prev, {
      title: '',
      firstName: '',
      middleName: '',
      lastName: '',
      bornOn: '',
      gender: '',
      nationality: '',
      phoneNumber: '',
      email: '',
      passportNumber: '',
      passportExpiryDate: '',
      frequentFlyerProgram: '',
      frequentFlyerNumber: '',
      knownTravelerNumber: '',
      specialRequests: '',
      // Seat preference cycle (W-M-A | A-M-W) — picks up where the
      // existing passenger list leaves off so groups added one-by-one
      // still get a sensible default seating spread.
      seatPreference: getDefaultSeatPreference(prev.length)
    }]);
  };

  const removePassenger = (index) => {
    if (editablePassengers.length > 1) {
      const newPassengers = editablePassengers.filter((_, i) => i !== index);
      setEditablePassengers(newPassengers);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior
    setIsProcessing(true);
    try {
      // Pass camelCase passengers directly to onSave
      // The Passenger entity will handle snake_case conversion for the database
      await onSave(editablePassengers);
    } catch (error) {
      console.error("Failed to save passenger data:", error);
      // Handle error (e.g., show a toast notification)
    } finally {
      setIsProcessing(false);
    }
  };
  
  const normalizePassengerForBlacklist = (p) => ({
    title: p.title || '',
    firstName: p.firstName || p.first_name || '',
    middleName: p.middleName || p.middle_name || '',
    lastName: p.lastName || p.last_name || '',
    bornOn: p.bornOn || p.born_on || '',
    gender: p.gender || '',
    nationality: p.nationality || '',
    phoneNumber: p.phoneNumber || p.phone_number || '',
    email: p.email || '',
    passportNumber: p.passportNumber || p.passport_number || '',
    passportExpiryDate: p.passportExpiryDate || p.passport_expiry || p.passport_expiry_date || '',
    frequentFlyerProgram: p.frequentFlyerProgram || p.frequent_flyer_program || '',
    frequentFlyerNumber: p.frequentFlyerNumber || p.frequent_flyer_number || '',
    knownTravelerNumber: p.knownTravelerNumber || p.known_traveler_number || '',
    specialRequests: p.specialRequests || p.special_requests || '',
    seatPreference: p.seatPreference || p.seat_preference || ''
  });
  
  const submitBlacklistPassenger = async (passenger, reasonText) => {
    if (!isAdmin) {
      toast.error('Admin access required');
      return;
    }
    
    try {
      setIsBlacklisting(true);
      const { supabase } = await import('@/lib/supabase');
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('Not authenticated');
      
      const response = await fetch('/api/admin/blacklist/add-passenger', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        credentials: 'omit',
        body: JSON.stringify({
          passenger: normalizePassengerForBlacklist(passenger),
          reason: reasonText,
          source_booking_id: bookingId,
          source_passenger_id: passenger.id || passenger.passengerId || null
        })
      });
      
      const text = await response.text();
      if (!response.ok) {
        let errMsg = text || `HTTP ${response.status}`;
        try {
          const json = JSON.parse(text);
          if (json.error) errMsg = json.error;
        } catch (_) {}
        throw new Error(errMsg);
      }
      
      toast.success('Passenger added to black list');
      setBlacklistConfirmPassenger(null);
    } catch (e) {
      console.error('Blacklist failed:', e);
      toast.error(e.message || 'Failed to blacklist passenger');
      setBlacklistConfirmPassenger(null);
    } finally {
      setIsBlacklisting(false);
    }
  };

  return (
    <motion.div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      
      <motion.div
        className="relative glass rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Edit Passenger Information</h2>
          <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/10 hover:text-white rounded-full">
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Wrap content in a form for proper submission handling */}
        <form onSubmit={handleSubmit}>
          <div className="space-y-6">
            {editablePassengers.map((passenger, index) => (
              <motion.div
                key={passenger.id || index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass-light rounded-xl p-5"
              >
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-white flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Passenger {index + 1}
                  </h3>
                  <div className="flex items-center gap-2">
                    {isAdmin && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setBlacklistConfirmPassenger(passenger);
                          setBlacklistReasonKey('defaulted_payments');
                          setBlacklistOtherReason('');
                        }}
                        className="bg-red-500/10 border-red-500/30 text-red-300 hover:bg-red-500/20 hover:border-red-500/40 h-8 text-xs"
                        title="Blacklist this passenger"
                      >
                        <ShieldAlert className="w-3.5 h-3.5 mr-1.5" />
                        Blacklist
                      </Button>
                    )}
                    {editablePassengers.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removePassenger(index)}
                        className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white">Title</Label>
                    <Select
                      value={passenger.title || ''}
                      onValueChange={(value) => handlePassengerChange(index, 'title', value)}
                    >
                      <SelectTrigger className="glass text-white border-white/20">
                        <SelectValue placeholder="Select title" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Mr">Mr</SelectItem>
                        <SelectItem value="Ms">Ms</SelectItem>
                        <SelectItem value="Mrs">Mrs</SelectItem>
                        <SelectItem value="Dr">Dr</SelectItem>
                        <SelectItem value="Prof">Prof</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">First Name</Label>
                    <Input
                      value={passenger.firstName || ''}
                      onChange={(e) => handlePassengerChange(index, 'firstName', e.target.value)}
                      className="glass text-white border-white/20 focus:border-white/40"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Middle Name</Label>
                    <Input
                      value={passenger.middleName || passenger.middle_name || ''}
                      onChange={(e) => handlePassengerChange(index, 'middleName', e.target.value)}
                      className="glass text-white border-white/20 focus:border-white/40"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Last Name</Label>
                    <Input
                      value={passenger.lastName || ''}
                      onChange={(e) => handlePassengerChange(index, 'lastName', e.target.value)}
                      className="glass text-white border-white/20 focus:border-white/40"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Date of Birth</Label>
                    <Input
                      type="date"
                      value={passenger.bornOn || ''}
                      onChange={(e) => handlePassengerChange(index, 'bornOn', e.target.value)}
                      className="glass text-white border-white/20 focus:border-white/40"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Gender</Label>
                    <Select
                      value={passenger.gender || ''}
                      onValueChange={(value) => handlePassengerChange(index, 'gender', value)}
                    >
                      <SelectTrigger className="glass text-white border-white/20">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Nationality</Label>
                    <Input
                      value={passenger.nationality || ''}
                      onChange={(e) => handlePassengerChange(index, 'nationality', e.target.value)}
                      className="glass text-white border-white/20 focus:border-white/40"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Phone Number</Label>
                    <Input
                      value={passenger.phoneNumber || ''}
                      onChange={(e) => handlePassengerChange(index, 'phoneNumber', e.target.value)}
                      className="glass text-white border-white/20 focus:border-white/40"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Email Address</Label>
                    <Input
                      type="email"
                      value={passenger.email || ''}
                      onChange={(e) => handlePassengerChange(index, 'email', e.target.value)}
                      className="glass text-white border-white/20 focus:border-white/40"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Passport Number</Label>
                    <Input
                      value={passenger.passportNumber || ''}
                      onChange={(e) => handlePassengerChange(index, 'passportNumber', e.target.value)}
                      className="glass text-white border-white/20 focus:border-white/40"
                    />
                  </div>
                   <div className="space-y-2">
                    <Label className="text-white">Passport Expiry</Label>
                    <Input
                      type="date"
                      value={passenger.passportExpiryDate || ''}
                      onChange={(e) => handlePassengerChange(index, 'passportExpiryDate', e.target.value)}
                      className="glass text-white border-white/20 focus:border-white/40"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Frequent Flyer Program</Label>
                    <Select
                      value={passenger.frequentFlyerProgram || ''}
                      onValueChange={(value) => handlePassengerChange(index, 'frequentFlyerProgram', value)}
                    >
                      <SelectTrigger className="glass text-white border-white/20">
                        <SelectValue placeholder="Select program..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {AIRLINE_PROGRAMS.map(program => (
                          <SelectItem key={program.value} value={program.value}>{program.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Frequent Flyer #</Label>
                    <Input
                      value={passenger.frequentFlyerNumber || ''}
                      onChange={(e) => handlePassengerChange(index, 'frequentFlyerNumber', e.target.value)}
                      className="glass text-white border-white/20 focus:border-white/40"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-white">Known Traveler #</Label>
                    <Input
                      value={passenger.knownTravelerNumber || ''}
                      onChange={(e) => handlePassengerChange(index, 'knownTravelerNumber', e.target.value)}
                      className="glass text-white border-white/20 focus:border-white/40"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label className="text-white">Seat Preference</Label>
                    <Select
                      value={passenger.seatPreference || passenger.seat_preference || getDefaultSeatPreference(index)}
                      onValueChange={(value) => handlePassengerChange(index, 'seatPreference', value)}
                    >
                      <SelectTrigger className="glass text-white border-white/20">
                        <SelectValue placeholder="Select preference" />
                      </SelectTrigger>
                      <SelectContent>
                        {SEAT_PREFERENCE_OPTIONS.map((opt) => (
                          <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  <Label className="text-white">Special Requests</Label>
                  <Textarea
                    value={passenger.specialRequests || ''}
                    onChange={(e) => handlePassengerChange(index, 'specialRequests', e.target.value)}
                    className="glass text-white border-white/20 focus:border-white/40"
                    rows={2}
                  />
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-6">
            <Button
              type="button"
              variant="outline"
              onClick={addPassenger}
              className="w-full glass border-white/20 text-white hover:bg-white/10 hover:text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Another Passenger
            </Button>
          </div>

          <div className="mt-8 flex justify-end gap-3 pt-6 border-t border-white/10">
            <Button
              type="button" // Use type="button" to prevent form submission
              variant="outline"
              onClick={onClose}
              className="glass border-white/20 text-white hover:bg-white/10 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              type="submit" // This button will trigger the form's onSubmit
              disabled={!isFormValid || isProcessing}
              className="bg-[var(--accent-color)] hover:bg-primary/90 text-white"
            >
              {isProcessing ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>

        {/* Blacklist confirmation overlay (acts as confirmation + reason selection) */}
        {blacklistConfirmPassenger && (
          <div className="fixed inset-0 z-[90] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-black/95 border border-red-500/30 rounded-3xl max-w-lg w-full p-7"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-white font-medium">Blacklist passenger</div>
                  <div className="text-white/60 text-sm font-normal mt-1">
                    Stores only unique identifiers (full name, DOB, email, phone, passport, frequent flyer). First or last name alone are not used. Any future match will block bookings.
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setBlacklistConfirmPassenger(null)}
                  className="text-white/60 hover:text-white hover:bg-white/10"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="mt-5 glass-light rounded-2xl p-4 border border-white/10">
                <div className="text-white text-sm font-medium">
                  {[
                    blacklistConfirmPassenger.firstName || blacklistConfirmPassenger.first_name || '',
                    blacklistConfirmPassenger.middleName || blacklistConfirmPassenger.middle_name || '',
                    blacklistConfirmPassenger.lastName || blacklistConfirmPassenger.last_name || ''
                  ].map(s => s.trim()).filter(Boolean).join(' ')}
                </div>
                <div className="text-white/50 text-xs font-normal mt-1">
                  {blacklistConfirmPassenger.email || '—'} · {blacklistConfirmPassenger.phoneNumber || blacklistConfirmPassenger.phone || '—'}
                </div>
              </div>

              <div className="mt-5">
                <Label className="text-white/80 text-xs">Reason</Label>
                <div className="mt-2">
                  <Select value={blacklistReasonKey} onValueChange={setBlacklistReasonKey}>
                    <SelectTrigger className="glass text-white border-white/20 focus:border-white/40">
                      <SelectValue placeholder="Select a reason" />
                    </SelectTrigger>
                    <SelectContent className="glass-light text-white border-white/10">
                      {BLACKLIST_REASONS.map(r => (
                        <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {blacklistReasonKey === 'other' && (
                  <div className="mt-3">
                    <Input
                      value={blacklistOtherReason}
                      onChange={(e) => setBlacklistOtherReason(e.target.value)}
                      className="glass text-white border-white/20 focus:border-white/40"
                      placeholder="Enter reason..."
                    />
                  </div>
                )}
              </div>

              <div className="mt-6 flex gap-3">
                <Button
                  type="button"
                  onClick={() => setBlacklistConfirmPassenger(null)}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white border border-white/10"
                  disabled={isBlacklisting}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={async () => {
                    const reasonText =
                      blacklistReasonKey === 'other'
                        ? (blacklistOtherReason || '').trim() || 'Other'
                        : (BLACKLIST_REASONS.find(r => r.value === blacklistReasonKey)?.label || 'Defaulted on payments');
                    await submitBlacklistPassenger(blacklistConfirmPassenger, reasonText);
                    setBlacklistConfirmPassenger(null);
                  }}
                  className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-100 border border-red-500/30"
                  disabled={isBlacklisting}
                >
                  {isBlacklisting ? 'Blacklisting…' : 'Confirm Blacklist'}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
